/* eslint-disable no-unused-vars */
'use client';

import { CaretDownIcon, CaretUpIcon, CheckIcon } from '@phosphor-icons/react';
import * as PopoverPrimitive from '@radix-ui/react-popover';
import * as SelectPrimitive from '@radix-ui/react-select';
import * as React from 'react';

import { cn } from '@/lib/utils';

interface SelectOption {
  value: string;
  label: string;
}

const DEFAULT_SEARCH_THRESHOLD = 10;

const selectOptionTextClassName =
  'justify-start text-gray-900 text-sm font-normal leading-tight truncate';

const selectTriggerValueClassName = 'flex-1 text-left font-normal leading-tight truncate';

const selectOptionItemClassName =
  'self-stretch w-full px-3.5 py-2.5 flex justify-between items-center gap-2 outline-none cursor-pointer transition-colors bg-white hover:bg-gray-200 data-highlighted:bg-gray-200 data-[state=checked]:bg-gray-200/90';

const selectOptionItemSelectedClassName = 'bg-gray-200/90';

interface SelectBaseProps {
  label?: string;
  placeholder?: string;
  options: SelectOption[];
  error?: string;
  disabled?: boolean;
  name?: string;
  size?: 'sm' | 'default';
  className?: string;
  searchable?: boolean;
  searchThreshold?: number;
  searchPlaceholder?: string;
  portalToBody?: boolean;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}

interface SingleSelectProps extends SelectBaseProps {
  multiple?: false;
  onValueChange?: (_value: string) => void;
  value?: string;
  defaultValue?: string;
  onChange?: (event: { target: { name?: string; value: string } }) => void;
  onBlur?: (event: { target: unknown; type?: unknown }) => void;
  multiValue?: never;
  onMultiValueChange?: never;
}

interface MultiSelectProps extends SelectBaseProps {
  multiple: true;
  multiValue: string[];
  onMultiValueChange: (value: string[]) => void;
  value?: never;
  defaultValue?: never;
  onValueChange?: never;
  onChange?: never;
  onBlur?: never;
}

type SelectProps = SingleSelectProps | MultiSelectProps;

function useSelectPortalContainer(
  triggerRef: React.RefObject<HTMLElement | null>,
  open: boolean,
  portalToBody: boolean,
) {
  const [container, setContainer] = React.useState<HTMLElement | undefined>(undefined);

  React.useLayoutEffect(() => {
    if (!open || portalToBody) {
      setContainer(undefined);
      return;
    }
    const dialog = triggerRef.current?.closest('[data-slot="dialog-content"]');
    setContainer(dialog instanceof HTMLElement ? dialog : undefined);
  }, [open, portalToBody, triggerRef]);

  if (!portalToBody && open && !container && triggerRef.current) {
    const dialog = triggerRef.current.closest('[data-slot="dialog-content"]');
    if (dialog instanceof HTMLElement) {
      return dialog;
    }
  }

  return container;
}

function Select(props: SelectProps) {
  const {
    label,
    placeholder,
    options,
    error,
    disabled,
    name,
    size = 'default',
    className,
    multiple,
  } = props;

  const shouldShowSearch =
    props.searchable ?? options.length > (props.searchThreshold ?? DEFAULT_SEARCH_THRESHOLD);

  if (multiple) {
    return (
      <MultiSelectInner
        label={label}
        placeholder={placeholder}
        options={options}
        error={error}
        disabled={disabled}
        size={size}
        className={className}
        value={props.multiValue}
        onValueChange={props.onMultiValueChange}
        searchable={shouldShowSearch}
        searchPlaceholder={props.searchPlaceholder}
        portalToBody={props.portalToBody}
        open={props.open}
        onOpenChange={props.onOpenChange}
      />
    );
  }

  const { onValueChange, value, defaultValue, onChange, onBlur, ...rest } =
    props as SingleSelectProps;

  if (shouldShowSearch) {
    return (
      <SearchableSingleSelectInner
        label={label}
        placeholder={placeholder}
        options={options}
        error={error}
        disabled={disabled}
        name={name}
        size={size}
        className={className}
        value={value}
        defaultValue={defaultValue}
        onValueChange={onValueChange}
        onChange={onChange}
        onBlur={onBlur}
        searchPlaceholder={props.searchPlaceholder}
      />
    );
  }

  const handleValueChange = (newValue: string) => {
    if (onChange) {
      onChange({ target: { name, value: newValue } });
    }
    if (onValueChange) {
      onValueChange(newValue);
    }
  };

  return (
    <div
      className={cn(
        'self-stretch inline-flex flex-col justify-start items-start gap-1.5',
        className,
      )}
    >
      <div className="self-stretch inline-flex flex-col justify-start items-start gap-1.5">
        {label && (
          <div className="self-stretch flex flex-col justify-start items-start gap-1.5">
            <div className="justify-start text-gray-700 text-sm font-medium leading-tight">
              {label}
            </div>
          </div>
        )}
        <div
          className={`input-shadow self-stretch rounded-lg outline-1 -outline-offset-1 inline-flex justify-start items-center gap-2 overflow-hidden input-shadow transition-colors ${
            disabled ? 'bg-gray-50' : 'bg-white'
          } ${
            error
              ? 'input-error outline-error-300 focus-within:outline-error-300'
              : 'outline-gray-300 focus-within:outline-blue-300'
          }`}
        >
          <SelectPrimitive.Root
            value={value}
            defaultValue={defaultValue}
            onValueChange={handleValueChange}
            disabled={disabled}
            name={name}
            {...rest}
          >
            <SelectTrigger size={size} onBlur={onBlur}>
              <SelectValue placeholder={placeholder} />
            </SelectTrigger>
            <SelectContent>
              {options.map((option) => (
                <SelectItem key={option.value} value={option.value}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </SelectPrimitive.Root>
        </div>
        {error && (
          <div className="self-stretch justify-start text-error-500 text-xs font-normal leading-none">
            {error}
          </div>
        )}
      </div>
    </div>
  );
}

function SelectSearchInput({
  value,
  onChange,
  placeholder = 'Buscar...',
  inputRef,
}: {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  inputRef?: React.RefObject<HTMLInputElement | null>;
}) {
  return (
    <div className="shrink-0 border-b border-gray-200 p-2">
      <input
        ref={inputRef}
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm outline-none transition-colors focus:border-blue-300"
        onKeyDown={(e) => e.stopPropagation()}
      />
    </div>
  );
}

function SearchableSingleSelectInner({
  label,
  placeholder = 'Seleccionar...',
  options,
  error,
  disabled,
  name,
  size = 'default',
  className,
  value,
  defaultValue,
  onValueChange,
  onChange,
  onBlur,
  searchPlaceholder = 'Buscar...',
  portalToBody = false,
  open: openProp,
  onOpenChange,
}: {
  label?: string;
  placeholder?: string;
  options: SelectOption[];
  error?: string;
  disabled?: boolean;
  name?: string;
  size?: 'sm' | 'default';
  className?: string;
  value?: string;
  defaultValue?: string;
  onValueChange?: (value: string) => void;
  onChange?: (event: { target: { name?: string; value: string } }) => void;
  onBlur?: (event: { target: unknown; type?: unknown }) => void;
  searchPlaceholder?: string;
  portalToBody?: boolean;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}) {
  const [uncontrolledOpen, setUncontrolledOpen] = React.useState(false);
  const open = openProp ?? uncontrolledOpen;
  const setOpen = (nextOpen: boolean) => {
    setUncontrolledOpen(nextOpen);
    onOpenChange?.(nextOpen);
  };
  const [searchQuery, setSearchQuery] = React.useState('');
  const searchInputRef = React.useRef<HTMLInputElement>(null);
  const triggerRef = React.useRef<HTMLButtonElement>(null);
  const portalContainer = useSelectPortalContainer(triggerRef, open, portalToBody);

  const currentValue = value ?? defaultValue;

  const selectedLabel = React.useMemo(() => {
    if (!currentValue) return '';
    return options.find((o) => o.value === currentValue)?.label ?? '';
  }, [currentValue, options]);

  const filteredOptions = React.useMemo(() => {
    const query = searchQuery.trim().toLowerCase();
    if (!query) return options;
    return options.filter((o) => o.label.toLowerCase().includes(query));
  }, [options, searchQuery]);

  const handleSelect = (optionValue: string) => {
    if (onChange) {
      onChange({ target: { name, value: optionValue } });
    }
    if (onValueChange) {
      onValueChange(optionValue);
    }
    setOpen(false);
  };

  React.useEffect(() => {
    if (!open) {
      setSearchQuery('');
      return;
    }
    requestAnimationFrame(() => searchInputRef.current?.focus());
  }, [open]);

  return (
    <div
      className={cn(
        'self-stretch inline-flex flex-col justify-start items-start gap-1.5',
        className,
      )}
    >
      <div className="self-stretch inline-flex flex-col justify-start items-start gap-1.5">
        {label && (
          <div className="self-stretch flex flex-col justify-start items-start gap-1.5">
            <div className="justify-start text-gray-700 text-sm font-medium leading-tight">
              {label}
            </div>
          </div>
        )}
        <PopoverPrimitive.Root
          modal={!portalContainer && !portalToBody}
          open={open}
          onOpenChange={setOpen}
        >
          <PopoverPrimitive.Trigger asChild disabled={disabled}>
            <button
              ref={triggerRef}
              type="button"
              onBlur={onBlur}
              className={cn(
                'input-shadow self-stretch rounded-lg outline-1 -outline-offset-1 inline-flex justify-start items-center gap-2 overflow-hidden transition-colors w-full group',
                size === 'sm'
                  ? 'text-sm font-normal px-3.5 py-2.5'
                  : 'text-md font-normal px-3.5 py-3 h-10',
                disabled ? 'bg-gray-50 cursor-not-allowed' : 'bg-white',
                error
                  ? 'input-error outline-error-300'
                  : open
                    ? 'outline-blue-300'
                    : 'outline-gray-300',
              )}
            >
              <span
                className={cn(
                  selectTriggerValueClassName,
                  size === 'sm' ? 'text-sm' : 'text-md',
                  selectedLabel ? 'text-gray-900' : 'text-gray-500',
                )}
              >
                {selectedLabel || placeholder}
              </span>
              <div className="size-4 opacity-50 relative ml-auto shrink-0">
                <CaretDownIcon
                  className={cn(
                    'size-4 absolute transition-opacity text-gray-500',
                    open && 'opacity-0',
                  )}
                  weight="bold"
                />
                <CaretUpIcon
                  className={cn(
                    'size-4 absolute transition-opacity text-gray-500',
                    !open && 'opacity-0',
                  )}
                  weight="bold"
                />
              </div>
            </button>
          </PopoverPrimitive.Trigger>

          <PopoverPrimitive.Portal container={portalContainer}>
            <PopoverPrimitive.Content
              data-slot="select-dropdown"
              align="start"
              side="bottom"
              sideOffset={8}
              avoidCollisions={false}
              onOpenAutoFocus={(event) => event.preventDefault()}
              onCloseAutoFocus={(event) => event.preventDefault()}
              onInteractOutside={(event) => {
                const target = event.target as Node;
                if (triggerRef.current?.contains(target)) {
                  event.preventDefault();
                }
              }}
              className="pointer-events-auto z-200 flex flex-col w-(--radix-popover-trigger-width) bg-white rounded-lg shadow-[0px_12px_16px_-4px_rgba(16,24,40,0.08)] outline-1 -outline-offset-1 outline-neutral-100 overflow-hidden text-sm mb-4 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95"
            >
              <SelectSearchInput
                inputRef={searchInputRef}
                value={searchQuery}
                onChange={setSearchQuery}
                placeholder={searchPlaceholder}
              />

              <div className="min-h-0 max-h-64 flex-1 overflow-y-auto">
                {filteredOptions.length > 0 ? (
                  filteredOptions.map((option) => {
                    const selected = currentValue === option.value;
                    return (
                      <button
                        key={option.value}
                        type="button"
                        onClick={() => handleSelect(option.value)}
                        className={cn(
                          selectOptionItemClassName,
                          'text-left font-normal',
                          selected && selectOptionItemSelectedClassName,
                        )}
                      >
                        <span className={selectOptionTextClassName}>{option.label}</span>
                        {selected && (
                          <CheckIcon className="size-4 shrink-0" style={{ color: '#7F56D9' }} />
                        )}
                      </button>
                    );
                  })
                ) : (
                  <div className="px-3.5 py-2.5 text-sm text-gray-500">
                    No se encontraron resultados
                  </div>
                )}
              </div>
            </PopoverPrimitive.Content>
          </PopoverPrimitive.Portal>
        </PopoverPrimitive.Root>
        {error && (
          <div className="self-stretch justify-start text-error-500 text-xs font-normal leading-none">
            {error}
          </div>
        )}
      </div>
    </div>
  );
}

function MultiSelectInner({
  label,
  placeholder = 'Seleccionar...',
  options,
  error,
  disabled,
  size = 'default',
  className,
  value,
  onValueChange,
  searchable = false,
  searchPlaceholder = 'Buscar...',
  portalToBody = false,
  open: openProp,
  onOpenChange,
}: {
  label?: string;
  placeholder?: string;
  options: SelectOption[];
  error?: string;
  disabled?: boolean;
  size?: 'sm' | 'default';
  className?: string;
  value: string[];
  onValueChange: (value: string[]) => void;
  searchable?: boolean;
  searchPlaceholder?: string;
  portalToBody?: boolean;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}) {
  const [uncontrolledOpen, setUncontrolledOpen] = React.useState(false);
  const open = openProp ?? uncontrolledOpen;
  const setOpen = (nextOpen: boolean) => {
    setUncontrolledOpen(nextOpen);
    onOpenChange?.(nextOpen);
  };
  const [searchQuery, setSearchQuery] = React.useState('');
  const searchInputRef = React.useRef<HTMLInputElement>(null);
  const triggerRef = React.useRef<HTMLButtonElement>(null);
  const portalContainer = useSelectPortalContainer(triggerRef, open, portalToBody);

  const allValues = React.useMemo(() => options.map((o) => o.value), [options]);

  const filteredOptions = React.useMemo(() => {
    if (!searchable) return options;
    const query = searchQuery.trim().toLowerCase();
    if (!query) return options;
    return options.filter((o) => o.label.toLowerCase().includes(query));
  }, [options, searchQuery, searchable]);

  React.useEffect(() => {
    if (!open) {
      setSearchQuery('');
      return;
    }
    if (searchable) {
      requestAnimationFrame(() => searchInputRef.current?.focus());
    }
  }, [open, searchable]);

  const isAllSelected = value.length === allValues.length && allValues.length > 0;
  const selectedLabels = React.useMemo(() => {
    if (isAllSelected) return 'Todos seleccionados';
    if (value.length === 0) return '';
    if (value.length === 1) return options.find((o) => o.value === value[0])?.label ?? '';
    return `${value.length} seleccionados`;
  }, [value, options, isAllSelected]);

  const toggle = (optionValue: string) => {
    const next = value.includes(optionValue)
      ? value.filter((v) => v !== optionValue)
      : [...value, optionValue];
    onValueChange(next);
  };

  const toggleAll = () => {
    if (isAllSelected) {
      onValueChange([]);
    } else {
      onValueChange(allValues);
    }
  };

  return (
    <div
      className={cn(
        'self-stretch inline-flex flex-col justify-start items-start gap-1.5',
        className,
      )}
    >
      <div className="self-stretch inline-flex flex-col justify-start items-start gap-1.5">
        {label && (
          <div className="self-stretch flex flex-col justify-start items-start gap-1.5">
            <div className="justify-start text-gray-700 text-sm font-medium leading-tight">
              {label}
            </div>
          </div>
        )}
        <PopoverPrimitive.Root
          modal={!portalContainer && !portalToBody}
          open={open}
          onOpenChange={setOpen}
        >
          <PopoverPrimitive.Trigger asChild disabled={disabled}>
            <button
              ref={triggerRef}
              type="button"
              className={cn(
                'input-shadow self-stretch rounded-lg outline-1 -outline-offset-1 inline-flex justify-start items-center gap-2 overflow-hidden transition-colors w-full group',
                size === 'sm'
                  ? 'text-sm font-normal px-3.5 py-2.5'
                  : 'text-md font-normal px-3.5 py-3 h-10',
                disabled ? 'bg-gray-50 cursor-not-allowed' : 'bg-white',
                error
                  ? 'input-error outline-error-300'
                  : open
                    ? 'outline-blue-300'
                    : 'outline-gray-300',
              )}
            >
              <span
                className={cn(
                  selectTriggerValueClassName,
                  size === 'sm' ? 'text-sm' : 'text-md',
                  'text-gray-900',
                )}
              >
                {selectedLabels || placeholder}
              </span>
              <div className="size-4 opacity-50 relative ml-auto shrink-0">
                <CaretDownIcon
                  className={cn(
                    'size-4 absolute transition-opacity text-gray-500',
                    open && 'opacity-0',
                  )}
                  weight="bold"
                />
                <CaretUpIcon
                  className={cn(
                    'size-4 absolute transition-opacity text-gray-500',
                    !open && 'opacity-0',
                  )}
                  weight="bold"
                />
              </div>
            </button>
          </PopoverPrimitive.Trigger>

          <PopoverPrimitive.Portal container={portalContainer}>
            <PopoverPrimitive.Content
              data-slot="select-dropdown"
              align="start"
              side="bottom"
              sideOffset={8}
              avoidCollisions={false}
              onOpenAutoFocus={(event) => event.preventDefault()}
              onCloseAutoFocus={(event) => event.preventDefault()}
              onInteractOutside={(event) => {
                const target = event.target as Node;
                if (triggerRef.current?.contains(target)) {
                  event.preventDefault();
                }
              }}
              className="pointer-events-auto z-200 flex flex-col w-(--radix-popover-trigger-width) bg-white rounded-lg shadow-[0px_12px_16px_-4px_rgba(16,24,40,0.08)] outline-1 -outline-offset-1 outline-neutral-100 overflow-hidden text-sm mb-4 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95"
            >
              {searchable && (
                <SelectSearchInput
                  inputRef={searchInputRef}
                  value={searchQuery}
                  onChange={setSearchQuery}
                  placeholder={searchPlaceholder}
                />
              )}

              <button
                type="button"
                onClick={toggleAll}
                className={cn(
                  'flex w-full shrink-0 items-center gap-2 px-3.5 py-2.5 text-left text-sm font-medium hover:bg-gray-50 transition-colors',
                  isAllSelected && 'bg-gray-50',
                )}
              >
                <div
                  className={cn(
                    'flex size-4 shrink-0 items-center justify-center rounded border transition-all duration-200',
                    isAllSelected
                      ? 'border-blue-600 bg-blue-50'
                      : 'border-gray-300 bg-white hover:border-blue-600',
                  )}
                >
                  {isAllSelected && <CheckIcon className="size-3 text-blue-600" />}
                </div>
                <span className="text-gray-900 truncate">Seleccionar todo</span>
              </button>

              <div className="self-stretch mx-3.5 h-px shrink-0 bg-gray-200" />

              <div className="min-h-0 max-h-48 flex-1 overflow-y-auto">
                {filteredOptions.length > 0 ? (
                  filteredOptions.map((option) => {
                    const selected = value.includes(option.value);
                    return (
                      <button
                        key={option.value}
                        type="button"
                        onClick={() => toggle(option.value)}
                        className={cn(
                          'flex w-full items-center gap-2 px-3.5 py-2.5 text-left text-sm hover:bg-gray-50 transition-colors',
                          selected && 'bg-gray-50',
                        )}
                      >
                        <div
                          className={cn(
                            'flex size-4 shrink-0 items-center justify-center rounded border transition-all duration-200',
                            selected
                              ? 'border-blue-600 bg-blue-50'
                              : 'border-gray-300 bg-white hover:border-blue-600',
                          )}
                        >
                          {selected && <CheckIcon className="size-3 text-blue-600" />}
                        </div>
                        <span className={selectOptionTextClassName}>{option.label}</span>
                      </button>
                    );
                  })
                ) : (
                  <div className="px-3.5 py-2.5 text-sm text-gray-500">
                    No se encontraron resultados
                  </div>
                )}
              </div>
            </PopoverPrimitive.Content>
          </PopoverPrimitive.Portal>
        </PopoverPrimitive.Root>
        {error && (
          <div className="self-stretch justify-start text-error-500 text-xs font-normal leading-none">
            {error}
          </div>
        )}
      </div>
    </div>
  );
}

function SelectGroup({ ...props }: React.ComponentProps<typeof SelectPrimitive.Group>) {
  return <SelectPrimitive.Group data-slot="select-group" {...props} />;
}

function SelectValue({ ...props }: React.ComponentProps<typeof SelectPrimitive.Value>) {
  return (
    <SelectPrimitive.Value
      data-slot="select-value"
      className="min-w-0 flex-1 truncate justify-start text-gray-900 text-sm font-normal leading-tight"
      {...props}
    />
  );
}

function SelectTrigger({
  className,
  size = 'default',
  children,
  ...props
}: React.ComponentProps<typeof SelectPrimitive.Trigger> & {
  size?: 'sm' | 'default';
}) {
  return (
    <SelectPrimitive.Trigger
      data-slot="select-trigger"
      data-size={size}
      className={cn(
        'flex flex-row min-w-0 w-full justify-start items-center gap-2 overflow-hidden data-placeholder:text-gray-500 group h-10',
        size === 'sm' ? 'text-sm px-3.5 py-2.5' : 'text-md px-3.5 py-3',
        className,
      )}
      {...props}
    >
      {children}
      <SelectPrimitive.Icon asChild>
        <div className="size-4 opacity-50 relative ml-auto">
          <CaretDownIcon
            className="size-4 absolute group-data-[state=open]:opacity-0 transition-opacity text-gray-500"
            weight="bold"
          />
          <CaretUpIcon
            className="size-4 absolute group-data-[state=closed]:opacity-0 transition-opacity text-gray-500"
            weight="bold"
          />
        </div>
      </SelectPrimitive.Icon>
    </SelectPrimitive.Trigger>
  );
}

function SelectContent({
  className,
  children,
  position = 'popper',
  ...props
}: React.ComponentProps<typeof SelectPrimitive.Content>) {
  return (
    <SelectPrimitive.Portal>
      <SelectPrimitive.Content
        data-slot="select-content"
        className={cn(
          'z-100 flex-1 inline-flex flex-col justify-start items-start w-80 bg-white rounded-lg shadow-[0px_12px_16px_-4px_rgba(16,24,40,0.08)] outline-1 -outline-offset-1 outline-neutral-100 overflow-hidden text-sm max-h-75 mb-4',
          className,
        )}
        position={position}
        side="bottom"
        sideOffset={8}
        {...props}
      >
        <SelectScrollUpButton />
        <SelectPrimitive.Viewport
          className={cn(
            'p-1 w-full overflow-y-auto flex-1',
            position === 'popper' &&
              'h-(--radix-select-trigger-height) w-full min-w-(--radix-select-trigger-width) scroll-my-1',
          )}
        >
          {children}
        </SelectPrimitive.Viewport>
        <SelectScrollDownButton />
      </SelectPrimitive.Content>
    </SelectPrimitive.Portal>
  );
}

function SelectLabel({ className, ...props }: React.ComponentProps<typeof SelectPrimitive.Label>) {
  return (
    <SelectPrimitive.Label
      data-slot="select-label"
      className={cn('text-muted-foreground px-2 py-1.5 text-xs', className)}
      {...props}
    />
  );
}

function SelectItem({
  className,
  children,
  ...props
}: React.ComponentProps<typeof SelectPrimitive.Item>) {
  return (
    <SelectPrimitive.Item
      data-slot="select-item"
      className={cn(selectOptionItemClassName, className)}
      {...props}
    >
      <SelectPrimitive.ItemText className={selectOptionTextClassName}>
        {children}
      </SelectPrimitive.ItemText>
      <span className="flex size-3.5 items-center justify-center">
        <SelectPrimitive.ItemIndicator>
          <CheckIcon className="size-4" style={{ color: '#7F56D9' }} />
        </SelectPrimitive.ItemIndicator>
      </span>
    </SelectPrimitive.Item>
  );
}

function SelectSeparator({
  className,
  ...props
}: React.ComponentProps<typeof SelectPrimitive.Separator>) {
  return (
    <SelectPrimitive.Separator
      data-slot="select-separator"
      className={cn('bg-border pointer-events-none -mx-1 my-1 h-px', className)}
      {...props}
    />
  );
}

function SelectScrollUpButton({
  className,
  ...props
}: React.ComponentProps<typeof SelectPrimitive.ScrollUpButton>) {
  return (
    <SelectPrimitive.ScrollUpButton
      data-slot="select-scroll-up-button"
      className={cn(
        'flex cursor-pointer items-center justify-center py-2 px-3 bg-gray-50 hover:bg-gray-100 transition-colors border-b border-gray-200',
        className,
      )}
      {...props}
    >
      <CaretUpIcon className="size-4 text-gray-600" />
    </SelectPrimitive.ScrollUpButton>
  );
}

function SelectScrollDownButton({
  className,
  ...props
}: React.ComponentProps<typeof SelectPrimitive.ScrollDownButton>) {
  return (
    <SelectPrimitive.ScrollDownButton
      data-slot="select-scroll-down-button"
      className={cn(
        'flex cursor-pointer items-center justify-center py-2 px-3 bg-gray-50 hover:bg-gray-100 transition-colors border-t border-gray-200',
        className,
      )}
      {...props}
    >
      <CaretDownIcon className="size-4 text-gray-600" />
    </SelectPrimitive.ScrollDownButton>
  );
}

export {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectScrollDownButton,
  SelectScrollUpButton,
  SelectSeparator,
  SelectTrigger,
  SelectValue,
};
